import { useCallback, useEffect, useState } from "react";
import Flyout from "@shared/v2/Flyout";
import Typography from "@shared/v2/Typography";
import useConferenceConfirmation from "../useConferenceConfirmation";
import ConferenceFlyoutCallForm from "./ConferenceFlyoutCallForm";
import {
  ConferenceConfirmationActions,
  ConferenceConfirmationBody,
  ConferenceConfirmationDescription,
} from "../ConferenceConfirmationShared";

interface ConferenceFlyoutProps {
  show: boolean;
  onClose: () => void;
  selectedLeadUuids: string[];
  createDialerSession: () => void;
  dialerServiceUrl: string;
  dialerServiceToken: string | null;
  isRecoveredSession?: boolean;
  isLoading: boolean;
  startCall?: boolean;
}

const MIN_FLYOUT_WIDTH_PX = 672;
const MIN_ENABLED_FLYOUT_WIDTH_PX = 800;
const MAX_FLYOUT_WIDTH_PX = 1400;
const DEFAULT_FLYOUT_VIEWPORT_WIDTH_RATIO = 0.8;

const getEffectiveMaxWidth = () => {
  if (typeof window === "undefined") return MIN_ENABLED_FLYOUT_WIDTH_PX;
  const responsiveWidth = Math.floor(window.innerWidth * DEFAULT_FLYOUT_VIEWPORT_WIDTH_RATIO);
  return Math.max(MIN_ENABLED_FLYOUT_WIDTH_PX, Math.min(MAX_FLYOUT_WIDTH_PX, responsiveWidth));
};

const clampFlyoutWidth = (width: number) => {
  const maxWidth = getEffectiveMaxWidth();
  return Math.min(Math.max(width, MIN_ENABLED_FLYOUT_WIDTH_PX), maxWidth);
};

const useFlyoutResize = (enabled: boolean) => {
  const [width, setWidth] = useState<number>(() => (enabled ? getEffectiveMaxWidth() : MIN_FLYOUT_WIDTH_PX));

  useEffect(() => {
    const handleResize = () => {
      setWidth((prev) => (enabled ? clampFlyoutWidth(prev) : MIN_FLYOUT_WIDTH_PX));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      setWidth(getEffectiveMaxWidth());
    } else {
      setWidth(MIN_FLYOUT_WIDTH_PX);
    }
  }, [enabled]);

  useEffect(
    () => () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
    },
    [],
  );

  const startResizing = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      e.preventDefault();

      document.body.style.userSelect = "none";
      document.body.style.cursor = "ew-resize";

      const handlePointerMove = (event: PointerEvent) => {
        setWidth(clampFlyoutWidth(window.innerWidth - event.clientX));
      };

      const handlePointerUp = () => {
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    },
    [enabled],
  );

  return { width, startResizing };
};

const ConferenceFlyout = ({
  show,
  onClose,
  selectedLeadUuids,
  createDialerSession,
  dialerServiceUrl,
  dialerServiceToken,
  isRecoveredSession = false,
  isLoading,
}: ConferenceFlyoutProps) => {
  const [enableLeadSummary, setEnableLeadSummary] = useState(true);
  const isCallFormVisible = Boolean(dialerServiceToken);
  const { width: flyoutWidth, startResizing } = useFlyoutResize(isCallFormVisible);

  const {
    consentChecked,
    featureFlags,
    isAdmin,
    loadingValidLeads,
    outboundNotificationMessage,
    outboundNotificationMessageCondition,
    outboundRecording,
    outboundRecordingEnabled,
    setConsentChecked,
    startDisabled,
    validLeadCounts,
  } = useConferenceConfirmation({ selectedLeadUuids, show });

  return (
    <Flyout show={show} onHide={onClose} closeOnClickOutside={false} closeOnEscape={false}>
      <Flyout.Body className="tw-relative tw-bg-white tw-h-full !tw-p-[24px]">
        {/* @ts-expect-error JSX child typing mismatch in this file */}
        <div className="tw-h-full tw-flex tw-flex-col" style={{ width: `${flyoutWidth}px` }}>
          {isCallFormVisible && (
            <button
              type="button"
              className="tw-absolute tw-left-[-6px] tw-top-0 tw-h-full tw-w-[6px] tw-cursor-ew-resize tw-z-20 tw-bg-transparent tw-border-0"
              onPointerDown={startResizing}
              aria-label="Resize conference flyout"
            />
          )}

          {isCallFormVisible && dialerServiceToken ? (
            <ConferenceFlyoutCallForm
              closeCallModal={onClose}
              dialerSession={{ dialerServiceUrl, dialerServiceToken, isRecoveredSession }}
              enableLeadSummary={enableLeadSummary}
            />
          ) : (
            <div className="tw-h-full tw-flex tw-flex-col tw-gap-[24px]">
              <div className="tw-flex tw-flex-col tw-gap-[4px]">
                {/* @ts-expect-error props */}
                <Typography variant="headline1">Brivity Dialer</Typography>
                <ConferenceConfirmationDescription />
              </div>

              <div className="tw-flex-1 tw-overflow-auto">
                <ConferenceConfirmationBody
                  consentChecked={consentChecked}
                  featureFlags={featureFlags}
                  isAdmin={isAdmin}
                  loadingValidLeads={loadingValidLeads}
                  outboundNotificationMessage={outboundNotificationMessage}
                  // @ts-expect-error props
                  outboundNotificationMessageCondition={outboundNotificationMessageCondition}
                  // @ts-expect-error props
                  outboundRecording={outboundRecording}
                  outboundRecordingEnabled={outboundRecordingEnabled}
                  setConsentChecked={setConsentChecked}
                  validLeadCounts={validLeadCounts}
                  enableLeadSummary={enableLeadSummary}
                  // @ts-expect-error props
                  setEnableLeadSummary={setEnableLeadSummary}
                />
              </div>

              <div className="tw-flex tw-justify-between tw-gap-[8px]">
                <ConferenceConfirmationActions
                  closeConfirmation={onClose}
                  createDialerSession={createDialerSession}
                  isLoading={isLoading}
                  startDisabled={startDisabled}
                />
              </div>
            </div>
          )}
        </div>
      </Flyout.Body>
    </Flyout>
  );
};

export default ConferenceFlyout;
